import ExcelJS from 'exceljs';
import { Project, Station, ProjectConfig, Route } from '../../types';
import { MatrixType, MATRIX_DEFINITIONS } from '../url/matrixMapping';
import { CURRENCIES } from '../../config/currencies';
import { getEffectivePopulation } from '../../utils/population';
import { getEffectiveDistance } from '../../utils/distance';
import cytoscape from 'cytoscape';
import { exportNetworkImage } from './files';
import { getSortedStationIndices, getSortedStations, reorderMatrix } from '../matrix/ordering';

interface ExcelExportOptions {
  project: Project;
  matrices: {
    cities: Station[];
    distances: number[][];
    flightDistances: number[][];
    flightTime: number[][];
    drivingDistances: number[][];
    drivingTime: number[][];
    gravity: number[][];
    ridership: number[][];
    trainsPerDay: number[][];
    trainsPerSegment: number[][];
    passengerKm: number[][];
    fares: number[][];
    businessFares: number[][];
    economyFares: number[][];
    discountFares: number[][];
    revenue: number[][];
    adjacency: number[][];
    constructionCost: number[][];
    passengersBySegment: number[][];
    passengersBySegmentKm: number[][];
    constructionCostPerTicket: number[][];
    travelTime: number[][];
    tracks: number[][];
    trainEmissions: number[][];
    flyingEmissions: number[][];
    drivingEmissions: number[][];
  };
  cy?: cytoscape.Core;
}

// Helper function to format currency values
function formatCurrency(value: number, currency: string): string {
  const currencyConfig = CURRENCIES[currency];
  return `${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencyConfig.code}`;
}

// Helper function to get Excel currency format string
function getExcelCurrencyFormat(currency: string): string {
  const currencyConfig = CURRENCIES[currency];
  return `"${currencyConfig.symbol}"#,##0.00`;
}

// Helper function to get Excel currency format string without decimals
function getExcelCurrencyFormatNoDecimals(currency: string): string {
  const currencyConfig = CURRENCIES[currency];
  return `"${currencyConfig.symbol}"#,##0`;
}

// Helper function to format duration values
function formatDuration(value: number): string {
  if (value === 0) return '0:00';
  const days = Math.floor(value);
  const hours = Math.floor((value - days) * 24);
  const minutes = Math.round(((value - days) * 24 - hours) * 60);
  if (days > 0) {
    return `${days}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

// Helper function to get Excel column letter from index (0-based)
function getExcelColumnLetter(index: number): string {
  return String.fromCharCode(65 + index); // A=0, B=1, etc.
}

// Helper function to get matrix range (NxN)
function getMatrixRange(startRow: number, numStations: number): string {
  const startCol = getExcelColumnLetter(1); // B column
  const endCol = getExcelColumnLetter(numStations);
  return `${startCol}${startRow}:${endCol}${startRow + numStations - 1}`;
}

// Helper function to get Cities sheet column range (single column)
function getCitiesColumnRange(startRow: number, numStations: number, column: string): string {
  return `${column}${startRow}:${column}${startRow + numStations - 1}`;
}

// Add this helper function at the top of the file
function sanitizeWorksheetName(name: string): string {
  // First remove colons
  name = name.replace(/:/g, '');
  // Then replace other invalid characters with spaces
  return name.replace(/[*?\\/[\]]/g, ' ');
}

// Helper function to create the calibration constants sheet
function createCalibrationConstantsSheet(
  workbook: ExcelJS.Workbook,
  project: Project,
  stations: Station[],
  matrices: any
) {
  const sheet = workbook.addWorksheet(sanitizeWorksheetName('Calibration Constants'));
  
  // Sort stations and reorder matrices
  const sortedStations = getSortedStations(stations);
  const sortedIndices = getSortedStationIndices(stations);
  const reorderedDistances = reorderMatrix(matrices.distances, sortedIndices);
  
  // Add headers with references to station names in Stations sheet
  const headerRow = sheet.addRow(['City']);
  headerRow.font = { bold: true };
  sortedStations.forEach((_, j) => {
    const cell = headerRow.getCell(j + 2);
    cell.value = { formula: `Cities!A${j + 2}` };
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'right' };
  });
  
  // Add data
  sortedStations.forEach((_, i) => {
    const dataRow = sheet.addRow([]);
    // Set first column to reference station name
    const firstCell = dataRow.getCell(1);
    firstCell.value = { formula: `Cities!A${i + 2}` };
    firstCell.font = { bold: true };
    
    // Add calibration constants
    sortedStations.forEach((_, j) => {
      const cell = dataRow.getCell(j + 2);
      
      // Find all routes that could be part of the shortest path between these stations
      const pathDistance = reorderedDistances[i][j];
      if (pathDistance === 0) {
        cell.value = { formula: 'Config!B20' }; // Domestic for same station
      } else {
        // Find all routes that could be part of the shortest path
        const connectingRoutes = project.network.routes.filter(route => {
          const sourceIndex = sortedStations.findIndex(s => s.id === route.source);
          const targetIndex = sortedStations.findIndex(s => s.id === route.target);
          
          // Check if this route is part of any shortest path between the stations
          // This includes routes that are part of the path through intermediate stations
          const effectiveDistance = getEffectiveDistance(route, project);
          const throughDistance = reorderedDistances[i][sourceIndex] + effectiveDistance + reorderedDistances[targetIndex][j];
          const alternatePath = reorderedDistances[i][targetIndex] + effectiveDistance + reorderedDistances[sourceIndex][j];
          
          // Use the same tolerance as the web app
          return Math.abs(pathDistance - throughDistance) < 0.1 || Math.abs(pathDistance - alternatePath) < 0.1;
        });
        
        // Determine which calibration constant to use based on route types
        // Check all routes in the path, including intermediate routes
        const hasCustomsRoute = connectingRoutes.some(r => r.landType === 'customs');
        const hasNoCustomsRoute = connectingRoutes.some(r => r.landType === 'no-customs');
        const hasDomesticRoute = connectingRoutes.some(r => r.landType === 'domestic');
        
        if (hasCustomsRoute) {
          cell.value = { formula: 'Config!B21' }; // Customs
        } else if (hasNoCustomsRoute) {
          cell.value = { formula: 'Config!B22' }; // No Customs
        } else if (hasDomesticRoute) {
          cell.value = { formula: 'Config!B20' }; // Domestic
        } else {
          // If no route type is specified, default to domestic
          cell.value = { formula: 'Config!B20' }; // Domestic
        }
      }
    });
  });
  
  // Set column widths
  sheet.getColumn(1).width = 20;
  for (let i = 2; i <= sortedStations.length + 1; i++) {
    sheet.getColumn(i).width = 20;
  }
  
  return sheet;
}

// Helper function to create a matrix sheet with formulas
function createMatrixSheet(
  workbook: ExcelJS.Workbook,
  name: string,
  matrix: number[][],
  stations: Station[],
  config?: ProjectConfig,
  type?: MatrixType
) {
  const sheet = workbook.addWorksheet(sanitizeWorksheetName(name));
  
  // Sort stations and reorder matrix
  const sortedStations = getSortedStations(stations);
  const sortedIndices = getSortedStationIndices(stations);
  const reorderedMatrix = reorderMatrix(matrix, sortedIndices);
  
  // Add headers with references to station names in Cities sheet
  const headerRow = sheet.addRow(['City']);
  headerRow.font = { bold: true };
  sortedStations.forEach((_, j) => {
    const cell = headerRow.getCell(j + 2);
    cell.value = { formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.cities.displayName)}'!A${j + 2}` };
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'right' };
  });
  
  // Add data
  sortedStations.forEach((_, i) => {
    const dataRow = sheet.addRow([]);
    // Set first column to reference station name
    const firstCell = dataRow.getCell(1);
    firstCell.value = { formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.cities.displayName)}'!A${i + 2}` };
    firstCell.font = { bold: true };
    
    // Add matrix data
    sortedStations.forEach((_, j) => {
      const cell = dataRow.getCell(j + 2);
      
      if (type === 'gravity') {
        // Gravity formula with minimum distance and proper population scaling
        // Return 0 when calculating travel within the same city (i === j)
        cell.value = {
          formula: `IF(ROW()=COLUMN(),0,'Calibration Constants'!${String.fromCharCode(66+j)}${i+2}*('${sanitizeWorksheetName(MATRIX_DEFINITIONS.cities.displayName)}'!B${i+2}/1000000*'${sanitizeWorksheetName(MATRIX_DEFINITIONS.cities.displayName)}'!B${j+2}/1000000)^Config!B23/(MAX('${sanitizeWorksheetName(MATRIX_DEFINITIONS.distances.displayName)}'!${String.fromCharCode(66+j)}${i+2},Config!B25)^Config!B24))`
        };
        cell.numFmt = '#,##0.0000';
      } else if (type === 'ridership') {
        // Ridership formula - multiply by 1000000 to scale back up from the gravity calculation
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.gravity.displayName)}'!${String.fromCharCode(66+j)}${i+2}*1000000`
        };
        cell.numFmt = '#,##0';
      } else if (type === 'trainsPerDay') {
        // Trains per day formula - using seats per train and load factor from Config
        // Config sheet structure:
        // B15 - Seats per Train (under Train Settings)
        // B16 - Load Factor (under Train Settings)
        cell.value = {
          formula: `IF('${sanitizeWorksheetName(MATRIX_DEFINITIONS.ridership.displayName)}'!${String.fromCharCode(66+j)}${i+2}=0,0,CEILING('${sanitizeWorksheetName(MATRIX_DEFINITIONS.ridership.displayName)}'!${String.fromCharCode(66+j)}${i+2}/365/(Config!B15*Config!B16),1))`
        };
        cell.numFmt = '#,##0';
      } else if (type === 'passengerKm') {
        // Passenger-kilometers formula - multiply ridership by distance
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.distances.displayName)}'!${String.fromCharCode(66+j)}${i+2}*'${sanitizeWorksheetName(MATRIX_DEFINITIONS.ridership.displayName)}'!${String.fromCharCode(66+j)}${i+2}`
        };
        cell.numFmt = '#,##0';
      } else if (type === 'fares') {
        // Base fares formula - calculate the economy fare per km using weighted average
        const economyFareFormula = `(Config!B7/((Config!B9*(1+Config!B10)+(1-Config!B9-Config!B11)+Config!B11*(1-Config!B12))))`;
        cell.value = {
          formula: `ROUND('${sanitizeWorksheetName(MATRIX_DEFINITIONS.distances.displayName)}'!${String.fromCharCode(66+j)}${i+2}*${economyFareFormula},0)`
        };
        cell.numFmt = getExcelCurrencyFormat(config?.bond.currency || 'USD');
      } else if (type === 'businessFares') {
        // Business fares formula - calculate economy fare per km and apply business premium
        const economyFareFormula = `(Config!B7/((Config!B9*(1+Config!B10)+(1-Config!B9-Config!B11)+Config!B11*(1-Config!B12))))`;
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.distances.displayName)}'!${String.fromCharCode(66+j)}${i+2}*${economyFareFormula}*(1+Config!B10)`
        };
        cell.numFmt = getExcelCurrencyFormatNoDecimals(config?.bond.currency || 'USD');
      } else if (type === 'economyFares') {
        // Economy fares formula - base calculation using weighted average
        const economyFareFormula = `(Config!B7/((Config!B9*(1+Config!B10)+(1-Config!B9-Config!B11)+Config!B11*(1-Config!B12))))`;
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.distances.displayName)}'!${String.fromCharCode(66+j)}${i+2}*${economyFareFormula}`
        };
        cell.numFmt = getExcelCurrencyFormatNoDecimals(config?.bond.currency || 'USD');
      } else if (type === 'discountFares') {
        // Discount fares formula - calculate economy fare per km and apply discount
        const economyFareFormula = `(Config!B7/((Config!B9*(1+Config!B10)+(1-Config!B9-Config!B11)+Config!B11*(1-Config!B12))))`;
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.distances.displayName)}'!${String.fromCharCode(66+j)}${i+2}*${economyFareFormula}*(1-Config!B12)`
        };
        cell.numFmt = getExcelCurrencyFormatNoDecimals(config?.bond.currency || 'USD');
      } else if (type === 'trainEmissions') {
        // Train emissions formula - multiply distance by CO2 factor
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.distances.displayName)}'!${String.fromCharCode(66+j)}${i+2}*0.01`
        };
        cell.numFmt = '#,##0.0';
      } else if (type === 'flyingEmissions') {
        // Flying emissions formula - multiply flight distance by CO2 factor
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.flightDistances.displayName)}'!${String.fromCharCode(66+j)}${i+2}*0.17`
        };
        cell.numFmt = '#,##0.0';
      } else if (type === 'drivingEmissions') {
        // Driving emissions formula - multiply driving distance by CO2 factor
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.drivingDistances.displayName)}'!${String.fromCharCode(66+j)}${i+2}*0.15`
        };
        cell.numFmt = '#,##0.0';
      } else if (type === 'revenue') {
        // Revenue formula - multiply ridership by weighted average fare
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.ridership.displayName)}'!${String.fromCharCode(66+j)}${i+2}*('${sanitizeWorksheetName(MATRIX_DEFINITIONS.businessFares.displayName)}'!${String.fromCharCode(66+j)}${i+2}*Config!B9+'${sanitizeWorksheetName(MATRIX_DEFINITIONS.economyFares.displayName)}'!${String.fromCharCode(66+j)}${i+2}*(1-Config!B9-Config!B11)+'${sanitizeWorksheetName(MATRIX_DEFINITIONS.discountFares.displayName)}'!${String.fromCharCode(66+j)}${i+2}*Config!B11)`
        };
        cell.numFmt = getExcelCurrencyFormatNoDecimals(config?.bond.currency || 'USD');
      } else if (type === 'constructionCost') {
        // Construction cost formula - multiply distance by cost per km
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.adjacency.displayName)}'!${String.fromCharCode(66+j)}${i+2}*Config!B2`
        };
        cell.numFmt = getExcelCurrencyFormatNoDecimals(config?.bond.currency || 'USD');
      } else if (type === 'constructionCostPerTicket') {
        // Construction cost per ticket formula - divide construction cost by ridership
        cell.value = {
          formula: `IF('${sanitizeWorksheetName(MATRIX_DEFINITIONS.ridership.displayName)}'!${String.fromCharCode(66+j)}${i+2}=0,0,'${sanitizeWorksheetName(MATRIX_DEFINITIONS.constructionCost.displayName)}'!${String.fromCharCode(66+j)}${i+2}/'${sanitizeWorksheetName(MATRIX_DEFINITIONS.ridership.displayName)}'!${String.fromCharCode(66+j)}${i+2})`
        };
        cell.numFmt = '0.000';
      } else if (type === 'travelTime') {
        // Travel time formula - convert hours to days
        cell.value = {
          formula: `'${sanitizeWorksheetName(MATRIX_DEFINITIONS.distances.displayName)}'!${String.fromCharCode(66+j)}${i+2}/Config!B17/24`
        };
        cell.numFmt = '[h]:mm';
      } else if (type === 'flightTime') {
        // Flight time formula - (distance/800 + 0.75 + 1.0) / 24 to convert to days
        cell.value = {
          formula: `IF('${sanitizeWorksheetName(MATRIX_DEFINITIONS.flightDistances.displayName)}'!${String.fromCharCode(66+j)}${i+2}=0,0,('${sanitizeWorksheetName(MATRIX_DEFINITIONS.flightDistances.displayName)}'!${String.fromCharCode(66+j)}${i+2}/800+0.75+1)/24)`
        };
        cell.numFmt = '[h]:mm';
      } else if (type === 'drivingTime') {
        // Driving time formula:
        // 1. Base driving time = distance/110
        // 2. Add 0.5h traffic delay
        // 3. Calculate rest stops based on total road time
        // 4. Convert to fraction of day
        cell.value = {
          formula: `IF('${sanitizeWorksheetName(MATRIX_DEFINITIONS.drivingDistances.displayName)}'!${String.fromCharCode(66+j)}${i+2}=0,0,
            LET(
              distance, '${sanitizeWorksheetName(MATRIX_DEFINITIONS.drivingDistances.displayName)}'!${String.fromCharCode(66+j)}${i+2},
              drivingHours, distance/110,
              roadTime, drivingHours + 0.5,
              restStops, FLOOR(roadTime/2,1),
              totalHours, roadTime + restStops*0.25,
              totalHours/24
            )
          )`
        };
        cell.numFmt = '[h]:mm';
      } else {
        // For other matrix types, use the raw value
        cell.value = reorderedMatrix[i][j];
        cell.numFmt = '#,##0';
      }
    });
  });
  
  // Set column widths
  sheet.getColumn(1).width = 20;
  for (let i = 2; i <= stations.length + 1; i++) {
    sheet.getColumn(i).width = 20;
  }
  
  return sheet;
}

// Helper function to create the summary sheet
async function createSummarySheet(
  workbook: ExcelJS.Workbook,
  project: Project,
  matrices: any,
  cy?: cytoscape.Core
) {
  const sheet = workbook.addWorksheet(sanitizeWorksheetName('Summary'));
  const numStations = project.network.stations.length;
  
  // Network Overview
  const networkOverviewRow = sheet.addRow(['Network Overview']);
  networkOverviewRow.font = { bold: true };
  
  const row2 = sheet.addRow(['Total Population']);
  row2.getCell(2).value = { formula: `SUM('${sanitizeWorksheetName(MATRIX_DEFINITIONS.cities.displayName)}'!${getCitiesColumnRange(2, numStations, 'B')})` };
  row2.getCell(2).numFmt = '#,##0';
  
  const row3 = sheet.addRow(['Network Length']);
  row3.getCell(2).value = { formula: `SUM('${sanitizeWorksheetName(MATRIX_DEFINITIONS.adjacency.displayName)}'!${getMatrixRange(2, numStations)})/2` };
  row3.getCell(2).numFmt = '#,##0';
  
  const row4 = sheet.addRow(['Construction Cost']);
  row4.getCell(2).value = { formula: `SUM('${sanitizeWorksheetName(MATRIX_DEFINITIONS.constructionCost.displayName)}'!${getMatrixRange(2, numStations)})/2` };
  
  // Annual Ridership
  sheet.addRow([]);
  const ridershipRow = sheet.addRow(['Annual Ridership']);
  ridershipRow.font = { bold: true };
  
  const row7 = sheet.addRow(['Total Passengers']);
  row7.getCell(2).value = { formula: `SUM('${sanitizeWorksheetName(MATRIX_DEFINITIONS.ridership.displayName)}'!${getMatrixRange(2, numStations)})/2` };
  row7.getCell(2).numFmt = '#,##0';
  
  const row8 = sheet.addRow(['Passenger-Kilometers']);
  row8.getCell(2).value = { formula: `SUM('${sanitizeWorksheetName(MATRIX_DEFINITIONS.passengerKm.displayName)}'!${getMatrixRange(2, numStations)})/2` };
  row8.getCell(2).numFmt = '#,##0';
  
  const row9 = sheet.addRow(['Passengers per Rail-km']);
  row9.getCell(2).value = { formula: 'B7/B3' };
  row9.getCell(2).numFmt = '#,##0';
  
  // Annual Finances
  sheet.addRow([]);
  const financesRow = sheet.addRow(['Annual Finances']);
  financesRow.font = { bold: true };
  
  const row12 = sheet.addRow(['Revenue']);
  row12.getCell(2).value = { formula: `SUM('${sanitizeWorksheetName(MATRIX_DEFINITIONS.revenue.displayName)}'!${getMatrixRange(2, numStations)})/2` };
  
  const row13 = sheet.addRow(['Operating Costs']);
  row13.getCell(2).value = { formula: 'B8*Config!B8' };
  
  const row14 = sheet.addRow(['Construction Loan Payment']);
  row14.getCell(2).value = { formula: '-PMT(Config!B4, Config!B3, B4)' };
  
  const row15 = sheet.addRow(['Net Income']);
  row15.getCell(2).value = { formula: 'B12-B13-B14' };
  
  const row16 = sheet.addRow(['Profit/Loss Percentage']);
  row16.getCell(2).value = { formula: 'B15/B12' };
  
  // Format currency cells with proper Excel format string
  ['B4', 'B12', 'B13', 'B14', 'B15'].forEach(cell => {
    sheet.getCell(cell).numFmt = getExcelCurrencyFormatNoDecimals(project.config.bond.currency);
  });
  
  // Format percentage cell
  sheet.getCell('B16').numFmt = '0.00%';
  
  // Set column widths
  sheet.getColumn('A').width = 35; // 250px
  sheet.getColumn('B').width = 20;

  // Add network image if Cytoscape instance is available
  if (cy) {
    try {
      const networkImage = await exportNetworkImage(cy);
      const imageBuffer = await networkImage.arrayBuffer();

      // Add image to sheet
      const image = workbook.addImage({
        buffer: new Uint8Array(imageBuffer),
        extension: 'png',
      });

      // Get the network's bounding box from Cytoscape
      const boundingBox = cy.elements().boundingBox({
        includeLabels: true,
        includeOverlays: true,
        includeEdges: true
      });

      // Calculate aspect ratio
      const aspectRatio = boundingBox.w / boundingBox.h;

      // Define maximum width in Excel units (approximately 1000 pixels)
      const maxExcelWidth = 1000;
      const excelHeight = maxExcelWidth / aspectRatio;

      // Position image at the top of the sheet with calculated dimensions
      sheet.addImage(image, {
        tl: { col: 3, row: 1 }, // 1 row from top (was 2)
        ext: { width: maxExcelWidth, height: excelHeight }
      });
    } catch (error) {
      console.error('Failed to add network image to Excel:', error);
    }
  }
  
  return sheet;
}

// Helper function to create the stations sheet
function createStationsSheet(
  workbook: ExcelJS.Workbook,
  project: Project,
  matrices: {
    cities: Station[];
    distances: number[][];
    gravity: number[][];
    ridership: number[][];
    trainsPerDay: number[][];
    trainsPerSegment: number[][];
    passengerKm: number[][];
    fares: number[][];
    businessFares: number[][];
    economyFares: number[][];
    discountFares: number[][];
    revenue: number[][];
    adjacency: number[][];
    constructionCost: number[][];
    passengersBySegment: number[][];
    passengersBySegmentKm: number[][];
    constructionCostPerTicket: number[][];
    travelTime: number[][];
    tracks: number[][];
  }
) {
  const sheet = workbook.addWorksheet(sanitizeWorksheetName(MATRIX_DEFINITIONS.cities.displayName));
  
  // Sort stations by population
  const sortedStations = getSortedStations(matrices.cities);
  const numStations = sortedStations.length;
  
  // Add headers
  const headers = ['City', 'Population within 30km', 'Arrivals', 'Revenue', 'Share of Total Revenue', 'Centrality'];
  const headerRow = sheet.addRow(headers);
  headerRow.font = { bold: true };
  
  // Set header alignment - right align all cells except the first one
  headers.forEach((_, index) => {
    const cell = headerRow.getCell(index + 1);
    if (index > 0) {
      cell.alignment = { horizontal: 'right' };
    }
  });
  
  // Add data
  sortedStations.forEach((station, i) => {
    const row = sheet.addRow([
      station.name,
      getEffectivePopulation(station),
      { formula: `SUM('${sanitizeWorksheetName(MATRIX_DEFINITIONS.ridership.displayName)}'!${String.fromCharCode(66)}${i+2}:${String.fromCharCode(65+numStations)}${i+2})/2` },
      { formula: `SUM('${sanitizeWorksheetName(MATRIX_DEFINITIONS.revenue.displayName)}'!${String.fromCharCode(66)}${i+2}:${String.fromCharCode(65+numStations)}${i+2})/2` },
      { formula: `D${i+2}/Summary!B12` }, // Share of Total Revenue
      { formula: `C${i+2}/B${i+2}^Config!B23` } // Centrality using density friction factor
    ]);
    
    // Format cells
    row.getCell(2).numFmt = '#,##0'; // Population
    row.getCell(3).numFmt = '#,##0'; // Arrivals
    row.getCell(4).numFmt = getExcelCurrencyFormatNoDecimals(project.config.bond.currency); // Revenue
    row.getCell(5).numFmt = '0.0%'; // Share of Total Revenue
    row.getCell(6).numFmt = '#,##0'; // Centrality - changed from '0.0' to '#,##0'
  });
  
  // Set column widths
  sheet.getColumn(1).width = 20; // City
  sheet.getColumn(2).width = 20; // Population
  sheet.getColumn(3).width = 20; // Arrivals
  sheet.getColumn(4).width = 20; // Revenue
  sheet.getColumn(5).width = 20; // Share of Total Revenue
  sheet.getColumn(6).width = 20; // Centrality
  
  return sheet;
}

// Helper function to create the config sheet
function createConfigSheet(
  workbook: ExcelJS.Workbook,
  project: Project
) {
  const sheet = workbook.addWorksheet(sanitizeWorksheetName('Config'));
  
  // Construction Cost section
  const constructionCostRow = sheet.addRow(['Construction Cost']);
  constructionCostRow.font = { bold: true };
  sheet.addRow(['Construction Cost per km', project.config.costs.constructionCostPerKm]);
  sheet.addRow(['Loan Duration (years)', project.config.bond.duration]);
  sheet.addRow(['Annual Interest Rate', project.config.bond.interestRate / 100]); // Convert to decimal
  
  // Ticket Price section
  sheet.addRow([]);
  const ticketPriceRow = sheet.addRow(['Ticket Price']);
  ticketPriceRow.font = { bold: true };
  sheet.addRow(['Fare per km', project.config.costs.farePerKm]);
  sheet.addRow(['Operating Cost per km', project.config.costs.operatingCostPerKm]);
  sheet.addRow(['Business Share of Seats', project.config.costs.businessShareOfSeats / 100]); // Convert to decimal
  sheet.addRow(['Business Ticket Premium', project.config.costs.businessTicketPremium / 100]); // Convert to decimal
  sheet.addRow(['Discount Share of Seats', project.config.costs.discountShareOfSeats / 100]); // Convert to decimal
  sheet.addRow(['Discount Ticket Reduction', project.config.costs.discountTicketReduction / 100]); // Convert to decimal
  
  // Train Settings
  sheet.addRow([]);
  const trainSettingsRow = sheet.addRow(['Train Settings']);
  trainSettingsRow.font = { bold: true };
  const seatsRow = sheet.addRow(['Seats per Train', project.config.trains.seatsPerTrain]);
  const loadFactorRow = sheet.addRow(['Load Factor', project.config.trains.loadFactor / 100]); // Convert to decimal
  const maxSpeedRow = sheet.addRow(['Max Speed (km/h)', project.config.trains.maxSpeed]);
  
  // Calculation Settings
  sheet.addRow([]);
  const calculationSettingsRow = sheet.addRow(['Calculation Settings']);
  calculationSettingsRow.font = { bold: true };
  sheet.addRow(['Calibration Constant', project.config.calculation.calibrationConstant]);
  sheet.addRow(['Calibration Constant (Customs)', project.config.calculation.calibrationConstantCustoms]);
  sheet.addRow(['Calibration Constant (No Customs)', project.config.calculation.calibrationConstantNoCustoms]);
  sheet.addRow(['Density Friction Factor', project.config.calculation.densityFrictionFactor]);
  sheet.addRow(['Distance Friction Factor', project.config.calculation.distanceFrictionFactor]);
  sheet.addRow(['Minimum Route Distance', project.config.calculation.minimumRouteDistance]);
  
  // Format currency cells with proper Excel format string
  sheet.getCell('B2').numFmt = getExcelCurrencyFormatNoDecimals(project.config.bond.currency);
  sheet.getCell('B7').numFmt = getExcelCurrencyFormat(project.config.bond.currency); // Fare per km
  sheet.getCell('B8').numFmt = getExcelCurrencyFormat(project.config.bond.currency); // Operating Cost per km
  
  // Format percentage cells
  sheet.getCell('B4').numFmt = '0.00%'; // Interest Rate
  sheet.getCell('B9').numFmt = '0%'; // Business Share of Seats
  sheet.getCell('B10').numFmt = '0%'; // Business Ticket Premium
  sheet.getCell('B11').numFmt = '0%'; // Discount Share of Seats
  sheet.getCell('B12').numFmt = '0%'; // Discount Ticket Reduction
  sheet.getCell('B16').numFmt = '0%'; // Load Factor
  
  // Format numbers with thousand separators
  seatsRow.getCell(2).numFmt = '#,##0';
  maxSpeedRow.getCell(2).numFmt = '#,##0';
  sheet.getCell('B20').numFmt = '#,##0'; // Calibration Constant
  sheet.getCell('B21').numFmt = '#,##0'; // Calibration Constant (Customs)
  sheet.getCell('B22').numFmt = '#,##0'; // Calibration Constant (No Customs)
  sheet.getCell('B24').numFmt = '#,##0'; // Minimum Route Distance
  
  // Set column widths
  sheet.getColumn('A').width = 35; // 250px
  sheet.getColumn('B').width = 20;
  
  return sheet;
}

export {
  createCalibrationConstantsSheet,
  createMatrixSheet,
  createSummarySheet,
  createStationsSheet,
  createConfigSheet
};

export async function generateExcelFile(options: ExcelExportOptions): Promise<ArrayBuffer> {
  const workbook = new ExcelJS.Workbook();
  
  // Sort stations using centralized ordering utilities
  const sortedIndices = getSortedStationIndices(options.project.network.stations);
  const sortedStations = getSortedStations(options.project.network.stations);

  // Reorder all matrices using centralized reorderMatrix utility
  const reorderedMatrices = {
    distances: reorderMatrix(options.matrices.distances, sortedIndices),
    flightDistances: reorderMatrix(options.matrices.flightDistances, sortedIndices),
    flightTime: reorderMatrix(options.matrices.flightTime, sortedIndices),
    drivingDistances: reorderMatrix(options.matrices.drivingDistances, sortedIndices),
    drivingTime: reorderMatrix(options.matrices.drivingTime, sortedIndices),
    adjacency: reorderMatrix(options.matrices.adjacency, sortedIndices),
    gravity: reorderMatrix(options.matrices.gravity, sortedIndices),
    ridership: reorderMatrix(options.matrices.ridership, sortedIndices),
    trainsPerDay: reorderMatrix(options.matrices.trainsPerDay, sortedIndices),
    passengerKm: reorderMatrix(options.matrices.passengerKm, sortedIndices),
    businessFares: reorderMatrix(options.matrices.businessFares, sortedIndices),
    economyFares: reorderMatrix(options.matrices.economyFares, sortedIndices),
    discountFares: reorderMatrix(options.matrices.discountFares, sortedIndices),
    revenue: reorderMatrix(options.matrices.revenue, sortedIndices),
    constructionCost: reorderMatrix(options.matrices.constructionCost, sortedIndices)
  };

  // Create sheets in order
  const summarySheet = await createSummarySheet(workbook, options.project, { ...options.matrices, cities: sortedStations }, options.cy);
  createConfigSheet(workbook, options.project);
  createStationsSheet(workbook, options.project, { ...options.matrices, cities: sortedStations });
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.distances.displayName, reorderedMatrices.distances, sortedStations);
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.adjacency.displayName, reorderedMatrices.adjacency, sortedStations);
  createCalibrationConstantsSheet(workbook, options.project, sortedStations, reorderedMatrices);
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.gravity.displayName, reorderedMatrices.gravity, sortedStations, options.project.config, 'gravity');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.ridership.displayName, reorderedMatrices.ridership, sortedStations, options.project.config, 'ridership');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.trainsPerDay.displayName, reorderedMatrices.trainsPerDay, sortedStations, options.project.config, 'trainsPerDay');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.passengerKm.displayName, reorderedMatrices.passengerKm, sortedStations, options.project.config, 'passengerKm');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.businessFares.displayName, reorderedMatrices.businessFares, sortedStations, options.project.config, 'businessFares');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.economyFares.displayName, reorderedMatrices.economyFares, sortedStations, options.project.config, 'economyFares');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.discountFares.displayName, reorderedMatrices.discountFares, sortedStations, options.project.config, 'discountFares');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.revenue.displayName, reorderedMatrices.revenue, sortedStations, options.project.config, 'revenue');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.constructionCost.displayName, reorderedMatrices.constructionCost, sortedStations, options.project.config, 'constructionCost');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.flightDistances.displayName, reorderedMatrices.flightDistances, sortedStations);
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.flightTime.displayName, reorderedMatrices.flightTime, sortedStations, options.project.config, 'flightTime');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.drivingDistances.displayName, reorderedMatrices.drivingDistances, sortedStations);
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.drivingTime.displayName, reorderedMatrices.drivingTime, sortedStations, options.project.config, 'drivingTime');
  
  // Add emissions sheets
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.trainEmissions.displayName, reorderedMatrices.distances, sortedStations, options.project.config, 'trainEmissions');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.flyingEmissions.displayName, reorderedMatrices.flightDistances, sortedStations, options.project.config, 'flyingEmissions');
  createMatrixSheet(workbook, MATRIX_DEFINITIONS.drivingEmissions.displayName, reorderedMatrices.drivingDistances, sortedStations, options.project.config, 'drivingEmissions');

  return workbook.xlsx.writeBuffer();
}